import type { DomainBlock, JsonValue } from "@note-app/contracts";
import { createBlockId } from "../create-id";

export const COLUMNS_SCHEMA_VERSION = 2 as const;

/**
 * The first persisted columns format. V1 already carried complete DomainBlock
 * values, but the UI only exposed a single plain-text paragraph for editing.
 */
export interface ColumnDataV1 {
  id: string;
  blocks: DomainBlock[];
}

export interface ColumnsDataV1 {
  schemaVersion: 1;
  columns: ColumnDataV1[];
}

/**
 * V2 makes the relative column width explicit and declares the nested block
 * arrays to be independently editable BlockNote documents. Unknown JSON keys
 * are retained when a V1 payload is migrated or a V2 payload is edited.
 */
export interface ColumnDataV2 {
  id: string;
  width: number;
  blocks: DomainBlock[];
}

export interface ColumnsDataV2 {
  schemaVersion: typeof COLUMNS_SCHEMA_VERSION;
  columns: ColumnDataV2[];
}

export type ColumnsData = ColumnsDataV2;

export type ColumnsDataErrorReason =
  | "invalid-json"
  | "invalid-data"
  | "unsupported-version";

export type ColumnsDataResult =
  | {
      ok: true;
      value: ColumnsDataV2;
      sourceVersion: 1 | typeof COLUMNS_SCHEMA_VERSION;
      migrated: boolean;
    }
  | {
      ok: false;
      reason: ColumnsDataErrorReason;
      message: string;
      schemaVersion?: number;
      raw: string;
    };

/**
 * Kept for compatibility with hosts that used the former textarea capability.
 * V2 rich columns should be edited through a nested block editor instead.
 */
export interface ColumnTextCapability {
  editable: boolean;
  text: string;
  reason?: "rich-content";
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function isJsonValue(value: unknown): value is JsonValue {
  if (
    value === null ||
    typeof value === "string" ||
    typeof value === "boolean"
  ) {
    return true;
  }
  if (typeof value === "number") {
    return Number.isFinite(value);
  }
  if (Array.isArray(value)) {
    return value.every(isJsonValue);
  }
  return isPlainRecord(value) && Object.values(value).every(isJsonValue);
}

function validateDomainBlock(
  value: unknown,
  blockIds: Set<string>,
): value is DomainBlock {
  if (!isPlainRecord(value)) {
    return false;
  }
  if (
    typeof value.id !== "string" ||
    value.id.length === 0 ||
    blockIds.has(value.id) ||
    typeof value.type !== "string" ||
    value.type.length === 0 ||
    !isPlainRecord(value.props) ||
    !isJsonValue(value.props) ||
    !isJsonValue(value.content) ||
    !Array.isArray(value.children)
  ) {
    return false;
  }
  blockIds.add(value.id);
  return value.children.every((child) =>
    validateDomainBlock(child, blockIds),
  );
}

function validateColumns(
  candidate: Record<string, unknown>,
  schemaVersion: 1 | typeof COLUMNS_SCHEMA_VERSION,
): boolean {
  if (
    !isJsonValue(candidate) ||
    !Array.isArray(candidate.columns) ||
    (candidate.columns.length !== 2 && candidate.columns.length !== 3)
  ) {
    return false;
  }

  const columnIds = new Set<string>();
  const blockIds = new Set<string>();
  for (const value of candidate.columns) {
    if (!isPlainRecord(value)) {
      return false;
    }
    if (
      typeof value.id !== "string" ||
      value.id.length === 0 ||
      columnIds.has(value.id) ||
      !Array.isArray(value.blocks)
    ) {
      return false;
    }
    if (
      schemaVersion === COLUMNS_SCHEMA_VERSION &&
      (typeof value.width !== "number" ||
        !Number.isFinite(value.width) ||
        value.width <= 0)
    ) {
      return false;
    }
    if (!value.blocks.every((block) => validateDomainBlock(block, blockIds))) {
      return false;
    }
    columnIds.add(value.id);
  }
  return true;
}

function migrateV1(value: ColumnsDataV1): ColumnsDataV2 {
  const width = 1 / value.columns.length;
  return {
    ...value,
    schemaVersion: COLUMNS_SCHEMA_VERSION,
    columns: value.columns.map((column) => ({
      ...column,
      width,
    })) as ColumnDataV2[],
  } as ColumnsDataV2;
}

export function createColumnsData(columnCount: 2 | 3 = 2): ColumnsDataV2 {
  const width = 1 / columnCount;
  return {
    schemaVersion: COLUMNS_SCHEMA_VERSION,
    columns: Array.from({ length: columnCount }, () => ({
      id: createBlockId(),
      width,
      blocks: [],
    })),
  };
}

export function serializeColumnsData(value: ColumnsDataV2): string {
  return JSON.stringify(value);
}

export function parseColumnsData(input: string): ColumnsDataResult {
  let parsed: unknown;
  try {
    parsed = JSON.parse(input);
  } catch (cause) {
    return {
      ok: false,
      reason: "invalid-json",
      message: cause instanceof Error ? cause.message : "Invalid columns JSON",
      raw: input,
    };
  }

  if (!isPlainRecord(parsed)) {
    return {
      ok: false,
      reason: "invalid-data",
      message: "Columns data must be an object",
      raw: input,
    };
  }

  if (
    typeof parsed.schemaVersion !== "number" ||
    !Number.isInteger(parsed.schemaVersion)
  ) {
    return {
      ok: false,
      reason: "invalid-data",
      message: "Columns data must declare an integer schemaVersion",
      raw: input,
    };
  }

  if (
    parsed.schemaVersion !== 1 &&
    parsed.schemaVersion !== COLUMNS_SCHEMA_VERSION
  ) {
    return {
      ok: false,
      reason: "unsupported-version",
      message: `Unsupported columns schema v${parsed.schemaVersion}`,
      schemaVersion: parsed.schemaVersion,
      raw: input,
    };
  }

  if (!validateColumns(parsed, parsed.schemaVersion)) {
    return {
      ok: false,
      reason: "invalid-data",
      message: "Invalid columns content",
      schemaVersion: parsed.schemaVersion,
      raw: input,
    };
  }

  if (parsed.schemaVersion === 1) {
    return {
      ok: true,
      value: migrateV1(parsed as unknown as ColumnsDataV1),
      sourceVersion: 1,
      migrated: true,
    };
  }

  return {
    ok: true,
    // Validation above proves the required fields. Keeping the original object
    // here is intentional: extensions owned by a newer producer are not
    // stripped merely because this renderer does not understand them.
    value: parsed as unknown as ColumnsDataV2,
    sourceVersion: COLUMNS_SCHEMA_VERSION,
    migrated: false,
  };
}

function inlineText(content: JsonValue): string {
  if (typeof content === "string") {
    return content;
  }
  if (Array.isArray(content)) {
    return content.map(inlineText).join("");
  }
  if (content && typeof content === "object") {
    const text = content.text;
    if (typeof text === "string") {
      return text;
    }
    const nested = content.content;
    if (nested !== undefined) {
      return inlineText(nested);
    }
  }
  return "";
}

function blockText(block: DomainBlock): string {
  return [
    inlineText(block.content),
    ...block.children.map(blockText),
  ]
    .filter(Boolean)
    .join("\n");
}

export function getColumnText(column: ColumnDataV1 | ColumnDataV2): string {
  return column.blocks.map(blockText).filter(Boolean).join("\n\n");
}

function isUnstyledText(value: unknown): boolean {
  if (!isPlainRecord(value)) {
    return false;
  }
  return (
    value.type === "text" &&
    typeof value.text === "string" &&
    isPlainRecord(value.styles) &&
    Object.keys(value.styles).length === 0
  );
}

export function getColumnTextCapability(
  column: ColumnDataV1 | ColumnDataV2,
): ColumnTextCapability {
  if (column.blocks.length === 0) {
    return { editable: true, text: "" };
  }
  const block = column.blocks[0];
  if (
    column.blocks.length !== 1 ||
    !block ||
    block.type !== "paragraph" ||
    block.children.length > 0 ||
    !Array.isArray(block.content) ||
    !block.content.every(isUnstyledText)
  ) {
    return {
      editable: false,
      text: getColumnText(column),
      reason: "rich-content",
    };
  }
  return { editable: true, text: getColumnText(column) };
}

/** @deprecated Use updateColumnBlocks from a nested rich-block editor. */
export function setColumnText(
  data: ColumnsDataV2,
  columnIndex: number,
  text: string,
): ColumnsDataV2 {
  const column = data.columns[columnIndex];
  if (!column || !getColumnTextCapability(column).editable) {
    return data;
  }

  const existing = column.blocks[0];
  const paragraph: DomainBlock = {
    id: existing?.id ?? createBlockId(),
    type: "paragraph",
    props: existing?.props ?? {},
    content:
      text.length === 0 ? [] : [{ type: "text", text, styles: {} }],
    children: [],
  };
  return updateColumnBlocks(
    data,
    columnIndex,
    text.length === 0 && existing === undefined ? [] : [paragraph],
  );
}

export function updateColumnBlocks(
  data: ColumnsDataV2,
  columnIndex: number,
  blocks: DomainBlock[],
): ColumnsDataV2 {
  if (!data.columns[columnIndex]) {
    return data;
  }
  return {
    ...data,
    columns: data.columns.map((column, index) =>
      index === columnIndex ? { ...column, blocks } : column,
    ),
  };
}

export function canResizeColumns(
  data: ColumnsDataV2,
  columnCount: 2 | 3,
): boolean {
  return (
    columnCount >= data.columns.length ||
    data.columns
      .slice(columnCount)
      .every((column) => column.blocks.length === 0)
  );
}

export function resizeColumns(
  data: ColumnsDataV2,
  columnCount: 2 | 3,
): ColumnsDataV2 {
  if (
    data.columns.length === columnCount ||
    !canResizeColumns(data, columnCount)
  ) {
    return data;
  }
  const empty = createColumnsData(columnCount);
  const width = 1 / columnCount;
  return {
    ...data,
    schemaVersion: COLUMNS_SCHEMA_VERSION,
    columns: empty.columns.map((column, index) => ({
      ...(data.columns[index] ?? column),
      width,
    })),
  };
}
